// =============================================================================
// Origin UI — src/components/compliance/FormFieldBuilder.tsx
// CM-13 — no-code form field builder. Edits an ordered list of fields
// (add → pick type → set label/help/required/validation/options → reorder/
// remove) and emits the canonical form-schema JSON (the same bare array the
// server validator and FormRenderer consume). A live preview renders via the
// FormRenderer so Compliance see exactly what staff will see — no JSON, no dev.
// =============================================================================

import { useEffect, useState } from 'react';
import type {
  ComplianceFormField,
  ComplianceFieldType,
  ComplianceFormOption,
} from '@/services/complianceApi';
import { FormRenderer } from './FormRenderer';
import type { AnswerMap } from './FormRenderer';
import styles from './FormFieldBuilder.module.css';

interface FormFieldBuilderProps {
  /** Initial schema JSON (raw string from an existing version), or empty. */
  initialSchema?: string | null;
  /** Called whenever the schema changes, with the serialised JSON string. */
  onChange: (schemaJson: string) => void;
}

const FIELD_TYPE_LABELS: Record<ComplianceFieldType, string> = {
  ShortText: 'Short text',
  LongText: 'Long text',
  Number: 'Number',
  Currency: 'Currency (USD)',
  Date: 'Date',
  SingleSelect: 'Single choice',
  MultiSelect: 'Multiple choice',
  Confirmation: 'Confirmation tick',
  StaticText: 'Section text',
};

const FIELD_TYPES = Object.keys(FIELD_TYPE_LABELS) as ComplianceFieldType[];

const hasOptions = (t: ComplianceFieldType) => t === 'SingleSelect' || t === 'MultiSelect';
const isNumeric = (t: ComplianceFieldType) => t === 'Number' || t === 'Currency';
const isText = (t: ComplianceFieldType) => t === 'ShortText' || t === 'LongText';

function slugKey(label: string, existing: string[]): string {
  const base =
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .split(' ')
      .map((w, i) => (i === 0 ? w : w.charAt(0).toUpperCase() + w.slice(1)))
      .join('') || 'field';
  let key = base;
  let n = 2;
  while (existing.includes(key)) {
    key = `${base}${n}`;
    n += 1;
  }
  return key;
}

export function FormFieldBuilder({ initialSchema, onChange }: FormFieldBuilderProps) {
  const [fields, setFields] = useState<ComplianceFormField[]>(() => {
    if (initialSchema) {
      try {
        const parsed = JSON.parse(initialSchema) as ComplianceFormField[];
        if (Array.isArray(parsed)) return parsed;
      } catch {
        /* ignore — start empty */
      }
    }
    return [];
  });

  const [previewValues, setPreviewValues] = useState<AnswerMap>({});

  // Emit serialised schema whenever fields change.
  useEffect(() => {
    onChange(JSON.stringify(fields));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);

  function update(index: number, patch: Partial<ComplianceFormField>) {
    setFields((prev) => prev.map((f, i) => (i === index ? { ...f, ...patch } : f)));
  }

  function addField() {
    setFields((prev) => [
      ...prev,
      { key: slugKey('field', prev.map((f) => f.key)), label: '', type: 'ShortText', required: false },
    ]);
  }

  function removeField(index: number) {
    setFields((prev) => prev.filter((_, i) => i !== index));
  }

  function move(index: number, dir: -1 | 1) {
    setFields((prev) => {
      const next = [...prev];
      const j = index + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[index], next[j]] = [next[j], next[index]];
      return next;
    });
  }

  function onLabelBlur(index: number) {
    // Auto-derive a stable key from the label if the key is still a default/empty.
    const f = fields[index];
    if (!f.label.trim()) return;
    const looksDefault = /^field\d*$/.test(f.key);
    if (looksDefault) {
      const others = fields.filter((_, i) => i !== index).map((x) => x.key);
      update(index, { key: slugKey(f.label, others) });
    }
  }

  // ── Options editing (single/multi select) ──
  function setOption(fieldIndex: number, optIndex: number, patch: Partial<ComplianceFormOption>) {
    setFields((prev) =>
      prev.map((f, i) => {
        if (i !== fieldIndex) return f;
        const opts = [...(f.options ?? [])];
        opts[optIndex] = { ...opts[optIndex], ...patch };
        return { ...f, options: opts };
      }),
    );
  }
  function addOption(fieldIndex: number) {
    setFields((prev) =>
      prev.map((f, i) =>
        i === fieldIndex
          ? { ...f, options: [...(f.options ?? []), { value: '', label: '' }] }
          : f,
      ),
    );
  }
  function removeOption(fieldIndex: number, optIndex: number) {
    setFields((prev) =>
      prev.map((f, i) =>
        i === fieldIndex
          ? { ...f, options: (f.options ?? []).filter((_, k) => k !== optIndex) }
          : f,
      ),
    );
  }

  return (
    <div className={styles.builder}>
      <div className={styles.columns}>
        {/* ── Editor ── */}
        <div className={styles.editor}>
          <div className={styles.editorHead}>
            <span className={styles.editorTitle}>Fields</span>
            <button type="button" className={styles.addBtn} onClick={addField}>
              + Add field
            </button>
          </div>

          {fields.length === 0 && (
            <div className={styles.empty}>No fields yet. Add one to start building the form.</div>
          )}

          {fields.map((f, i) => (
            <div key={i} className={styles.fieldCard}>
              <div className={styles.fieldCardHead}>
                <span className={styles.fieldNum}>{i + 1}</span>
                <select
                  className={styles.typeSelect}
                  value={f.type}
                  onChange={(e) => {
                    const t = e.target.value as ComplianceFieldType;
                    const patch: Partial<ComplianceFormField> = { type: t };
                    if (hasOptions(t) && !f.options) patch.options = [{ value: '', label: '' }];
                    update(i, patch);
                  }}
                >
                  {FIELD_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {FIELD_TYPE_LABELS[t]}
                    </option>
                  ))}
                </select>
                <div className={styles.fieldCardActions}>
                  <button type="button" className={styles.iconBtn} onClick={() => move(i, -1)} disabled={i === 0} title="Move up">
                    ↑
                  </button>
                  <button type="button" className={styles.iconBtn} onClick={() => move(i, 1)} disabled={i === fields.length - 1} title="Move down">
                    ↓
                  </button>
                  <button type="button" className={styles.iconBtnDanger} onClick={() => removeField(i)} title="Remove">
                    ✕
                  </button>
                </div>
              </div>

              <div className={styles.fieldRow}>
                <label className={styles.miniLabel}>
                  {f.type === 'StaticText' ? 'Text' : 'Label'}
                </label>
                <input
                  className={styles.miniInput}
                  value={f.label}
                  onChange={(e) => update(i, { label: e.target.value })}
                  onBlur={() => onLabelBlur(i)}
                  placeholder={f.type === 'StaticText' ? 'Guidance shown to the user' : 'Question / field label'}
                />
              </div>

              {f.type !== 'StaticText' && (
                <>
                  <div className={styles.fieldRow}>
                    <label className={styles.miniLabel}>Help text</label>
                    <input
                      className={styles.miniInput}
                      value={f.helpText ?? ''}
                      onChange={(e) => update(i, { helpText: e.target.value || undefined })}
                      placeholder="Optional"
                    />
                  </div>

                  <div className={styles.inlineRow}>
                    <label className={styles.checkLabel}>
                      <input
                        type="checkbox"
                        checked={!!f.required}
                        onChange={(e) => update(i, { required: e.target.checked })}
                      />
                      Required
                    </label>

                    {isText(f.type) && (
                      <label className={styles.inlineField}>
                        Max length
                        <input
                          type="number"
                          className={styles.numInput}
                          value={f.maxLength ?? ''}
                          onChange={(e) =>
                            update(i, { maxLength: e.target.value ? Number(e.target.value) : undefined })
                          }
                        />
                      </label>
                    )}

                    {isNumeric(f.type) && (
                      <>
                        <label className={styles.inlineField}>
                          Min
                          <input
                            type="number"
                            className={styles.numInput}
                            value={f.min ?? ''}
                            onChange={(e) =>
                              update(i, { min: e.target.value ? Number(e.target.value) : undefined })
                            }
                          />
                        </label>
                        <label className={styles.inlineField}>
                          Max
                          <input
                            type="number"
                            className={styles.numInput}
                            value={f.max ?? ''}
                            onChange={(e) =>
                              update(i, { max: e.target.value ? Number(e.target.value) : undefined })
                            }
                          />
                        </label>
                        <label className={styles.inlineField}>
                          Decimals
                          <input
                            type="number"
                            className={styles.numInput}
                            value={f.decimalPlaces ?? ''}
                            onChange={(e) =>
                              update(i, { decimalPlaces: e.target.value ? Number(e.target.value) : undefined })
                            }
                          />
                        </label>
                      </>
                    )}

                    {f.type === 'Date' && (
                      <label className={styles.checkLabel}>
                        <input
                          type="checkbox"
                          checked={f.maxDate === 'today'}
                          onChange={(e) => update(i, { maxDate: e.target.checked ? 'today' : undefined })}
                        />
                        No future dates
                      </label>
                    )}
                  </div>

                  {hasOptions(f.type) && (
                    <div className={styles.optionsBlock}>
                      <label className={styles.miniLabel}>Choices</label>
                      {(f.options ?? []).map((opt, oi) => (
                        <div key={oi} className={styles.optionRow}>
                          <input
                            className={styles.miniInput}
                            value={opt.label}
                            onChange={(e) => {
                              const label = e.target.value;
                              // Default the stored value to the label unless explicitly set.
                              setOption(i, oi, {
                                label,
                                value: opt.value && opt.value !== '' ? opt.value : label,
                              });
                            }}
                            placeholder="Option shown to the user"
                          />
                          <button
                            type="button"
                            className={styles.iconBtnDanger}
                            onClick={() => removeOption(i, oi)}
                            title="Remove choice"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                      <button type="button" className={styles.addOptionBtn} onClick={() => addOption(i)}>
                        + Add choice
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>

        {/* ── Live preview ── */}
        <div className={styles.preview}>
          <span className={styles.editorTitle}>Live preview</span>
          <div className={styles.previewCard}>
            {fields.length === 0 ? (
              <div className={styles.empty}>The form preview appears here.</div>
            ) : (
              <FormRenderer fields={fields} values={previewValues} onChange={setPreviewValues} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
